import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {SimpleTagValue} from 'schemas/tags/TagValue';
import legacyRunModal from 'ui-library/legacyRunModal';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {ScrollableTableColumn, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import * as WorkflowDetailsTableReduxActions from 'workflows/components/WorkflowDetailsTableReduxActions';
import {
  openConfigureStageRunsModalToBulkCreateStageRuns,
  openConfigureStageRunsModalToCreateStageRun,
  openConfigureStageRunsModalToDuplicateBranch,
  openConfigureStageRunsModalToEditStageRun,
} from 'workflows/modals/ConfigureStageRunsModalUtil';
import SetExperimentalConditionsModal from 'workflows/modals/SetExperimentalConditionsModal';
import * as StageRunActions from 'workflows/StageRunActions';
import {
  experimentalConditionValuesToString,
  getExperimentalConditionValueNames,
  stageConfigurationHasExperimentalConditionValues,
} from 'workflows/util/ExperimentalConditionUtil';
import {getFieldDefinitions, getFieldValueCells} from 'workflows/util/StageRunUtil';
import {
  getShouldDisableDetailsTableHovering,
  getShouldExpandFirstStageOnly,
} from 'workflows/util/WorkflowUtil';
import {getNodeHighlightMode, HighlightMode} from 'workflows/visualization/StageFlowNodeHighlightUtil';
import {StageRunGraph} from 'workflows/visualization/TaggedNodeGraph';
import {
  StageConfiguration,
  StageEntryDocument,
  StageEntrySummaryData,
  StageRun,
  StageRunSample,
  Workflow,
  WorkflowStage,
  WorkflowStructuredData,
} from 'workflows/WorkflowConstants';

import * as WorkflowVisualizationReduxActions from '../visualization/WorkflowVisualizationReduxActions';
import StageHeaderCell from './StageHeaderCell';
import StageRunDescription from './StageRunDescription';
import StageRunSampleLink from './StageRunSampleLink';
import StructuredFieldHeader from './StructuredFieldHeader';
import {getSummaryStats} from './WorkflowDetailsTableUtil';

interface PropsFromReduxState {
  clickedUpstreamNodeId: string | null;
  clickedDownstreamNodeId: string | null;
  hoveredNodeId: string | null;
  isStageExpandedById: {[stageId: string]: boolean};
}

function mapStateToProps(
  state: BenchlingState,
  {stageIdToStageRuns}: {stageIdToStageRuns: {[stageId: string]: Array<StageRun>}}
): PropsFromReduxState {
  // Disable hovering for high-throughput workflows due to performance
  const hoveredNodeId = getShouldDisableDetailsTableHovering(stageIdToStageRuns)
    ? null
    : state.ui.workflowVisualization.hoveredNodeId;
  return {
    clickedUpstreamNodeId: state.ui.workflowVisualization.clickedUpstreamNodeId,
    clickedDownstreamNodeId: state.ui.workflowVisualization.clickedDownstreamNodeId,
    hoveredNodeId,
    isStageExpandedById: state.ui.workflowDetailsTable.isStageExpandedById,
  };
}

interface PropsFromDispatch {
  collapseStages: (stageIds: Array<string>) => void;
  expandStages: (stageIds: Array<string>) => void;
  onHoverNode: ((nodeId: string) => void) | null;
  onUnhoverNode: ((nodeId: string) => void) | null;
}

function mapDispatchToProps(
  dispatch: BenchlingDispatch,
  {stageIdToStageRuns}: {stageIdToStageRuns: {[stageId: string]: Array<StageRun>}}
): PropsFromDispatch {
  if (getShouldDisableDetailsTableHovering(stageIdToStageRuns)) {
    return {
      onHoverNode: null,
      onUnhoverNode: null,
      expandStages(stageIds: Array<string>): void {
        dispatch(WorkflowDetailsTableReduxActions.expandStages(stageIds));
      },
      collapseStages(stageIds: Array<string>): void {
        dispatch(WorkflowDetailsTableReduxActions.collapseStages(stageIds));
      },
    };
  } else {
    return {
      onHoverNode(nodeId: string): void {
        dispatch(WorkflowVisualizationReduxActions.hoverNode(nodeId));
      },
      onUnhoverNode(nodeId: string): void {
        dispatch(WorkflowVisualizationReduxActions.unhoverNode(nodeId));
      },
      expandStages(stageIds: Array<string>): void {
        dispatch(WorkflowDetailsTableReduxActions.expandStages(stageIds));
      },
      collapseStages(stageIds: Array<string>): void {
        dispatch(WorkflowDetailsTableReduxActions.collapseStages(stageIds));
      },
    };
  }
}

interface WorkflowDetailsTableRow extends ScrollableTableRow {
  stageRun?: StageRun | null;
}

interface WorkflowDetailsTableProps extends PropsFromReduxState, PropsFromDispatch {
  isExpanded: boolean;
  stageIdToStageEntries: {[stageId: string]: Array<StageEntryDocument>};
  stageIdToStageRuns: {[stageId: string]: Array<StageRun>};
  stageIdToStageConfiguration: {[stageId: string]: StageConfiguration};
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
  stageIdToStructuredData: {[stageId: string]: WorkflowStructuredData};
  stageRunGraph: StageRunGraph;
  workflowStages: Array<WorkflowStage>;
  workflow: Workflow;
  isWorkflowEditable: boolean;
  entryIdToSummaryData?: {[entryId: string]: StageEntrySummaryData};
}

export class WorkflowDetailsTable extends React.Component<WorkflowDetailsTableProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  override componentDidMount(): void {
    const shouldExpandFirstStageOnly = getShouldExpandFirstStageOnly(this.props.stageIdToStageRuns);
    if (shouldExpandFirstStageOnly) {
      this.props.expandStages([this.props.workflowStages[0].id]);
    } else {
      // eslint-disable-next-line no-restricted-syntax
      this.props.expandStages(_.pluck(this.props.workflowStages, 'id'));
    }
  }

  _handleSetIsStageCollapsed(isStageCollapsed: boolean, stageId: string): void {
    if (isStageCollapsed) {
      this.props.collapseStages([stageId]);
    } else {
      this.props.expandStages([stageId]);
    }
  }

  _handleOpenCreateStageRunModal = (workflowStage: WorkflowStage): void => {
    openConfigureStageRunsModalToCreateStageRun(
      this.props.stageRunGraph,
      workflowStage.stageConfiguration.index,
      {workflowId: this.props.workflow.id}
    );
  };

  _handleOpenEditStageRunModal = (stageRun: StageRun): void => {
    openConfigureStageRunsModalToEditStageRun(this.props.stageRunGraph, stageRun, {
      workflowId: this.props.workflow.id,
    });
  };

  async _handleOpenSetExperimentalCondition(workflowStage: WorkflowStage, stageRun: StageRun): Promise<void> {
    await legacyRunModal(SetExperimentalConditionsModal, {
      stageFlowNode: stageRun.stageFlowNode,
      stageConfiguration: this.props.stageIdToStageConfiguration[workflowStage.id],
      onRequestSubmit: (experimentalConditionValueIds: Array<string>) => {
        StageRunActions.bulkConfigureStageRuns({
          editSpecifiers: [{stageRunId: stageRun.id, experimentalConditionValueIds}],
        });
      },
    });
  }

  _renderSamples(sampleIds: Array<string>, noSamplesText: string): JSX.Element {
    if (sampleIds.length > 0) {
      for (const sampleId of sampleIds) {
        if (this.props.stageRunSampleById[sampleId] == null) {
          return <StageRunSampleLink sample={null} />;
        }
      }
      return (
        <span>
          <StageRunSampleLink sample={this.props.stageRunSampleById[sampleIds[0]]} key={sampleIds[0]} />
          {/* eslint-disable-next-line no-restricted-syntax */}
          {_.rest(sampleIds, 1).map((sampleId) => (
            <span key={sampleId}>
              <span>, </span>
              <StageRunSampleLink sample={this.props.stageRunSampleById[sampleId]} />
            </span>
          ))}
        </span>
      );
    }
    return <div className="workflowDetailsTable-missingCellValue">{noSamplesText}</div>;
  }

  _getExperimentalConditionValueNames(stageId: string): Array<string> {
    return getExperimentalConditionValueNames(this.props.stageIdToStageRuns[stageId]);
  }

  _getFieldDefinitions(stageId: string): Array<{tableName: string; name: string}> {
    return getFieldDefinitions(this.props.stageIdToStructuredData[stageId]);
  }

  _getAdditionalHeaderCells(stageId: string): Array<JSX.Element | string> {
    const experimentalConditionValueNameCells = this._getExperimentalConditionValueNames(stageId);
    const structuredFieldNameCells = this._getFieldDefinitions(stageId).map((fieldDefinition) => (
      <StructuredFieldHeader fieldDefinition={fieldDefinition} />
    ));
    return [...experimentalConditionValueNameCells, ...structuredFieldNameCells];
  }

  _getAdditionalRowCells(stageId: string, stageRun: StageRun): Array<string | JSX.Element> {
    const experimentalConditionValueCells = this._getExperimentalConditionValueNames(stageId).map((name) => {
      const experimentalConditionValues = stageRun.stageFlowNode.experimentalConditionValues.filter(
        (value) => value.name === name
      );
      return experimentalConditionValues.length > 0 ? (
        experimentalConditionValuesToString(experimentalConditionValues)
      ) : (
        <span className="u-faded">None</span>
      );
    });

    const stageRunFields = getFieldValueCells(this.props.stageIdToStructuredData[stageId], stageRun.id);
    const structuredFieldCells = this._getFieldDefinitions(stageId).map((fieldDefinition) => {
      // eslint-disable-next-line no-restricted-syntax
      const cellFields = _.filter(
        stageRunFields,
        (field) => field.tableName === fieldDefinition.tableName && field.name === fieldDefinition.name
      );
      return (
        <SimpleTagValue
          // eslint-disable-next-line no-restricted-syntax
          tag={{value: _.pluck(cellFields, 'value').join(', ')}}
          // eslint-disable-next-line no-restricted-syntax
          className={_.any(_.pluck(cellFields, 'important')) ? 'u-background-active' : ''}
        />
      );
    });

    return [...experimentalConditionValueCells, ...structuredFieldCells];
  }

  // Return the maximum number of additional columns among all the workflow stages,
  // where an additional column is either a structured field or an experimental condition.
  _getAdditionalColumnsCount(): number {
    return Math.max(
      ...this.props.workflowStages.map(
        (stage) =>
          this._getExperimentalConditionValueNames(stage.id).length +
          this._getFieldDefinitions(stage.id).length
      )
    );
  }

  _getColumns(): Array<ScrollableTableColumn> {
    const columns: Array<ScrollableTableColumn> = [
      {
        header: '',
        accessorKey: 'stageRun',
        isCollapsible: false,
        isSortable: false,
        width: 307, // @width--sidepanel - @nyc-gutter - 1
        isFixed: true,
      },
    ];

    if (this.props.isExpanded) {
      columns.push({
        header: '',
        // Represents summary stats when stage is collapsed
        accessorKey: 'input',
        isCollapsible: false,
        isSortable: false,
        width: 250,
      });
      columns.push({
        header: '',
        accessorKey: 'output',
        isCollapsible: false,
        isSortable: false,
        flexGrow: 1,
        width: 200,
      });
      for (let i = 0; i < this._getAdditionalColumnsCount(); i++) {
        columns.push({
          header: '',
          accessorKey: `field${i}`,
          isCollapsible: false,
          isSortable: false,
          flexGrow: 1,
        });
      }
    }

    return columns;
  }

  _handleBulkCreateStageRuns = (stageConfiguration: StageConfiguration): void => {
    openConfigureStageRunsModalToBulkCreateStageRuns(this.props.stageRunGraph, stageConfiguration.index, {
      workflowId: this.props.workflow.id,
    });
  };

  _handleBulkSplitStageRuns = (workflowStage: WorkflowStage): void => {
    StageRunActions.splitByInputs(workflowStage);
  };

  _renderStageHeaderCell(
    workflowStage: WorkflowStage,
    {isStageCollapsed}: {isStageCollapsed: boolean}
  ): JSX.Element {
    const stageConfiguration = this.props.stageIdToStageConfiguration[workflowStage.id];
    return (
      <StageHeaderCell
        baseWorkflowTemplateVersionId={this.props.workflow.baseWorkflowTemplateVersionId}
        onBulkCreateStageRuns={this._handleBulkCreateStageRuns}
        onBulkSplitStageRuns={this._handleBulkSplitStageRuns}
        onCreate={this._handleOpenCreateStageRunModal}
        onSetIsStageCollapsed={(newIsStageCollapsed) => {
          this._handleSetIsStageCollapsed(newIsStageCollapsed, workflowStage.id);
        }}
        isStageCollapsed={isStageCollapsed}
        stageConfiguration={stageConfiguration}
        workflowStage={workflowStage}
      />
    );
  }

  _getHeaderRowForWorkflowStage(workflowStage: WorkflowStage): WorkflowDetailsTableRow {
    const keyedHeaderCells = _.chain(this._getAdditionalHeaderCells(workflowStage.id))
      .map((cell, index) => [`field${index}`, cell])
      .object()
      .value();
    const data = {
      stageRun: this._renderStageHeaderCell(workflowStage, {isStageCollapsed: false}),
      input: 'Input',
      output: 'Output',
      ...keyedHeaderCells,
    };
    return {
      id: workflowStage.id,
      data,
      className: 'workflowDetailsTable-headerRow',
    };
  }

  _getCollapsedHeaderRowForWorkflowStage(workflowStage: WorkflowStage): WorkflowDetailsTableRow {
    const stageRuns = this.props.stageIdToStageRuns[workflowStage.id];
    const summaryStats = getSummaryStats(stageRuns);
    return {
      id: workflowStage.id,
      data: {
        stageRun: this._renderStageHeaderCell(workflowStage, {isStageCollapsed: true}),
        input: <div className="workflowDetailsTable-summaryStats">{summaryStats}</div>,
      },
      className: 'workflowDetailsTable-headerRow workflowDetailsTable-headerRow--collapsed',
    };
  }

  _handleCopySelectedPath(stageRun: StageRun): void {
    openConfigureStageRunsModalToDuplicateBranch(this.props.stageRunGraph, stageRun, {
      workflowId: this.props.workflow.id,
    });
  }

  _getRowForStageRun({
    workflowStage,
    stageRun,
    isLastRow,
  }: {
    workflowStage: WorkflowStage;
    stageRun: StageRun;
    isLastRow: boolean;
  }): WorkflowDetailsTableRow {
    const summaryData =
      this.props.entryIdToSummaryData == null
        ? null
        : this.props.entryIdToSummaryData[stageRun.stageEntry.id];
    const hasImportant = summaryData != null && summaryData.hasImportant;
    let noInputSamplesText;
    if (stageRun.stageFlowNode.parentIds.length === 0) {
      noInputSamplesText = 'None';
    } else {
      noInputSamplesText = 'Pending';
    }

    const keyedRowCells = _.chain(this._getAdditionalRowCells(workflowStage.id, stageRun))
      .map((cell, index) => [`field${index}`, cell])
      .object()
      .value();
    const data = {
      stageRun: (
        <StageRunDescription
          isHovered={stageRun.id === this.props.hoveredNodeId}
          onEditStageRun={this._handleOpenEditStageRunModal}
          onCopySelectedPath={() => {
            this._handleCopySelectedPath(stageRun);
          }}
          onOpenSetExperimentalCondition={(editingStageRun) => {
            this._handleOpenSetExperimentalCondition(workflowStage, editingStageRun);
          }}
          openSetExperimentalConditionDisabled={
            !stageConfigurationHasExperimentalConditionValues(
              this.props.stageIdToStageConfiguration[workflowStage.id]
            )
          }
          stageEntry={
            _.findWhere(this.props.stageIdToStageEntries[workflowStage.id], {
              id: stageRun.stageEntry.id,
            })!
          }
          hasImportant={hasImportant}
          stageRun={stageRun}
          isStageEditable={workflowStage.permissions.writable}
          isWorkflowEditable={this.props.isWorkflowEditable}
        />
      ),

      input: this._renderSamples(stageRun.inputSampleIds, noInputSamplesText),
      output: this._renderSamples(stageRun.outputSampleIds, 'None'),
      ...keyedRowCells,
    };
    return {
      id: stageRun.id,
      className: classNames('workflowDetailsTable-row', {
        'workflowDetailsTable-lastRow': isLastRow,
      }),
      data,
      isSelected: stageRun.id === this.props.hoveredNodeId,
      stageRun,
    };
  }

  _getRowsForWorkflowStage(workflowStage: WorkflowStage): Array<WorkflowDetailsTableRow> {
    const stageRuns = _.sortBy(this.props.stageIdToStageRuns[workflowStage.id], 'createdAt');
    if (!stageRuns || stageRuns.length === 0) {
      return [
        {
          id: `${workflowStage.id}: NO_STAGE_RUNS`,
          data: {
            stageRun: (
              <div className="workflowDetailsTable-noStageRuns">
                <div className="stageRunDescription-stageRunLinkVisualization" />
                <span className="workflowDetailsTable-noStageRuns-text">No stage runs</span>
              </div>
            ),
            input: <span className="u-faded">{'\u2014'}</span>,
            output: <span className="u-faded">{'\u2014'}</span>,
            entry: <span className="u-faded">{'\u2014'}</span>,
          },
          className: 'workflowDetailsTable-row workflowDetailsTable-lastRow',
        },
      ];
    }
    const visibleStageRuns = stageRuns.filter(
      (stageRun) =>
        getNodeHighlightMode(stageRun.id, {
          clickedUpstreamNodeId: this.props.clickedUpstreamNodeId,
          clickedDownstreamNodeId: this.props.clickedDownstreamNodeId,
          taggedNodeGraph: this.props.stageRunGraph,
        }) !== HighlightMode.DEHIGHLIGHTED
    );
    return visibleStageRuns.map((stageRun, index) =>
      this._getRowForStageRun({
        workflowStage,
        stageRun,
        isLastRow: index === visibleStageRuns.length - 1,
      })
    );
  }

  _getRows(): Array<WorkflowDetailsTableRow> {
    // eslint-disable-next-line no-restricted-syntax
    return _.flatten(
      this.props.workflowStages.map((workflowStage, index) => {
        if (!this.props.isStageExpandedById[workflowStage.id]) {
          const collapsedHeaderRow = this._getCollapsedHeaderRowForWorkflowStage(workflowStage);
          return [collapsedHeaderRow];
        }
        const rows = this._getRowsForWorkflowStage(workflowStage);
        if (!rows.length) {
          // All stage runs in this stage have been filtered out
          return [];
        } else {
          const headerRow = this._getHeaderRowForWorkflowStage(workflowStage);
          return [headerRow, ...rows];
        }
      })
    );
  }

  _handleRowMouseEnter = (row: WorkflowDetailsTableRow): void => {
    if (this.props.onHoverNode && row.stageRun) {
      this.props.onHoverNode(row.stageRun.id);
    }
  };

  _handleRowMouseLeave = (row: WorkflowDetailsTableRow): void => {
    if (this.props.onUnhoverNode && row.stageRun) {
      this.props.onUnhoverNode(row.stageRun.id);
    }
  };

  override render(): JSX.Element {
    return (
      <ExpandedScrollableTable
        allowOverflow={false}
        className={classNames('workflowDetailsTable', {'is-collapsed': !this.props.isExpanded})}
        columns={this._getColumns()}
        hasLineBelowHeader={false}
        isCompact={false}
        matchParentHeight={true}
        onRowMouseEnter={this._handleRowMouseEnter}
        onRowMouseLeave={this._handleRowMouseLeave}
        rows={this._getRows()}
      />
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowDetailsTable);
